// Players (plan §3.3, §7). One screen, two tabs — no new route:
//   • Directory  — every member (except you), from GET /api/players, each with
//                  a live presence dot, their rating, and one-tap Play (ask #2).
//   • Leaderboard — the per-speed Glicko-2 ranking (unchanged render).

import { api, ApiError, type DirectoryEntry, type RankEntry, type User } from "../api.ts";
import { esc, h } from "../dom.ts";
import { navigate } from "../router.ts";
import { currentUser } from "../session.ts";
import { isOnline, onlineUsers, onSessionMessage } from "../presence.ts";
import { topNav } from "../ui/topnav.ts";
import { pagedList } from "../ui/pagedList.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { quickChallenge, savePrefs, type ChallengePrefs } from "../ui/quickChallenge.ts";
import { t } from "../i18n/index.ts";

const SPEED_LABELS: [string, string][] = [
  ["blitz", "chess.blitz"],
  ["rapid", "chess.rapid"],
];

export async function playersScreen(): Promise<HTMLElement> {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  const el = h(`
    <main class="card wide">
      <section class="panel">
        <div class="tabs" role="tablist">
          <button class="tab-btn active" role="tab" data-tab="directory">${t("players.directory")}</button>
          <button class="tab-btn" role="tab" data-tab="leaderboard">${t("players.leaderboard")}</button>
        </div>
        <div class="tab-body"></div>
      </section>
    </main>
  `);
  el.prepend(topNav("players"));

  const body = el.querySelector<HTMLElement>(".tab-body")!;
  const tabs = el.querySelector<HTMLElement>(".tabs")!;

  const show = (tab: string) => {
    tabs.querySelectorAll<HTMLElement>(".tab-btn").forEach((b) =>
      b.classList.toggle("active", b.dataset.tab === tab),
    );
    body.replaceChildren();
    if (tab === "leaderboard") void renderLeaderboard(body, user.username);
    else renderDirectory(body, user);
  };

  tabs.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>(".tab-btn");
    if (btn?.dataset.tab) show(btn.dataset.tab);
  });

  show("directory");
  return el;
}

// ---------------------------------------------------------------------------
// Directory tab (full roster + live presence)
// ---------------------------------------------------------------------------

function renderDirectory(body: HTMLElement, user: User): void {
  const wrap = h(`
    <div class="directory">
      <details class="challenge-opts">
        <summary>${t("players.gameOptions")}<span class="opts-summary"></span></summary>
        <div class="quick-slot"></div>
      </details>
      <input class="dir-search" type="search" enterkeyhint="search" autocomplete="off"
             aria-label="${t("players.searchLabel")}" placeholder="${t("players.searchPlaceholder")}" />
      <div class="online-strip" hidden></div>
      <div class="recent-chips" hidden></div>
      <div class="dir-list-slot"></div>
      <p class="fine"><a data-link href="/invite">${t("players.inviteEmail")}</a></p>
    </div>`);
  body.append(wrap);

  // Shared challenge settings for every one-tap Play (remembered across visits).
  const qc = quickChallenge();
  wrap.querySelector<HTMLElement>(".quick-slot")!.append(qc.element);
  const summary = wrap.querySelector<HTMLElement>(".opts-summary")!;
  const paintSummary = () => (summary.textContent = describePrefs(qc.read()));
  qc.element.addEventListener("change", paintSummary);
  paintSummary();

  const searchInput = wrap.querySelector<HTMLInputElement>(".dir-search")!;
  const strip = wrap.querySelector<HTMLElement>(".online-strip")!;
  const chips = wrap.querySelector<HTMLElement>(".recent-chips")!;
  const listSlot = wrap.querySelector<HTMLElement>(".dir-list-slot")!;

  // One-tap challenge using the current (possibly just-tweaked) options.
  const challenge = async (name: string, btn: HTMLButtonElement) => {
    const prefs = qc.read();
    btn.disabled = true;
    const prev = btn.textContent;
    btn.textContent = "…";
    try {
      const res = await api.challengeMember(name, prefs.tc, prefs.color, prefs.rated);
      savePrefs(prefs);
      navigate(`/wait/${res.token}`);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = prev;
      const note = document.createElement("span");
      note.className = "msg bad";
      note.textContent = err instanceof ApiError ? err.message : t("player.cantChallenge");
      btn.after(note);
      window.setTimeout(() => note.remove(), 4000);
    }
  };

  const makeRow = (e: DirectoryEntry): HTMLElement => {
    const online = isOnline(e.id);
    const row = document.createElement("div");
    row.className = "dir-row";
    row.dataset.uid = e.id; // keyed lookup for O(1) presence-dot updates
    row.innerHTML = `
      <span class="presence-dot ${online ? "on" : ""}" title="${online ? t("players.online") : t("players.offline")}"></span>
      <a class="dir-name" data-link href="/player/${encodeURIComponent(e.username)}">${esc(e.username)}</a>
      <span class="dir-meta">${esc(ratingText(e))}</span>`;
    const play = document.createElement("button");
    play.type = "button";
    play.className = "btn primary sm";
    play.textContent = t("players.play");
    play.addEventListener("click", () => challenge(e.username, play));
    row.append(play);
    return row;
  };

  // "Online now" strip (plan item 1): who's around, always one glance, independent
  // of which roster page you're on — fed straight from the presence socket. Chips
  // link to the profile (where the group-gated Challenge button lives).
  const paintOnline = () => {
    const others = onlineUsers().filter((u) => u.id !== user.id);
    strip.replaceChildren();
    if (others.length === 0) {
      strip.hidden = true;
      return;
    }
    others.sort((a, b) => a.username.localeCompare(b.username));
    const label = document.createElement("span");
    label.className = "chips-label";
    label.textContent = t("players.onlineNow");
    strip.append(label);
    for (const u of others) {
      const a = document.createElement("a");
      a.className = "online-chip";
      a.setAttribute("data-link", "");
      a.href = `/player/${encodeURIComponent(u.username)}`;
      a.innerHTML = `<span class="presence-dot on"></span>${esc(u.username)}`;
      strip.append(a);
    }
    strip.hidden = false;
  };

  // Recently-played chips: members you've actually played, most recent first (the
  // server already ordered the first page that way), one-tap rematch. Only shown
  // on the unfiltered roster (a search narrows the intent away from "recent").
  const paintRecent = (items: DirectoryEntry[]) => {
    chips.replaceChildren();
    const recent = query ? [] : items.filter((e) => e.last_played_at != null).slice(0, 6);
    if (recent.length === 0) {
      chips.hidden = true;
      return;
    }
    const label = document.createElement("span");
    label.className = "chips-label";
    label.textContent = t("players.recentlyPlayed");
    chips.append(label);
    for (const e of recent) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "chip";
      chip.textContent = e.username;
      chip.title = t("players.challengeName", { who: e.username });
      chip.addEventListener("click", () => challenge(e.username, chip));
      chips.append(chip);
    }
    chips.hidden = false;
  };

  // Update a single roster row's dot by id (O(changed), not O(n)) — the churn fix.
  const setDot = (id: string, online: boolean) => {
    for (const row of paged.rows.children) {
      const el = row as HTMLElement;
      if (el.dataset.uid !== id) continue;
      const dot = el.querySelector<HTMLElement>(".presence-dot");
      if (dot) {
        dot.classList.toggle("on", online);
        dot.title = online ? t("players.online") : t("players.offline");
      }
      return;
    }
  };
  // On a full presence snapshot (reconnect), refresh every rendered dot.
  const refreshAllDots = () => {
    for (const row of paged.rows.children) {
      const el = row as HTMLElement;
      const id = el.dataset.uid;
      if (!id) continue;
      const dot = el.querySelector<HTMLElement>(".presence-dot");
      if (!dot) continue;
      const on = isOnline(id);
      dot.classList.toggle("on", on);
      dot.title = on ? t("players.online") : t("players.offline");
    }
  };

  let query = "";
  const paged = pagedList<DirectoryEntry>({
    fetchPage: (offset, limit) =>
      api.players(limit, offset, query || undefined).then((r) => ({ items: r.players, has_more: r.has_more })),
    renderRow: makeRow,
    emptyText: () => (query ? t("players.searchEmpty", { q: query }) : t("players.empty")),
    errorText: t("players.loadFailed"),
    onPage: (items, append) => {
      if (!append) paintRecent(items);
    },
  });
  listSlot.append(paged.element);

  // Debounced search → refetch with ?q, restarting from page 1.
  let debounce: number | undefined;
  searchInput.addEventListener("input", () => {
    if (debounce) window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      const next = searchInput.value.trim();
      if (next === query) return;
      query = next;
      void paged.reload();
    }, 200);
  });

  // Presence deltas touch one dot (keyed) instead of rebuilding all 999 rows;
  // the Online strip repaints from the live set.
  const off = onSessionMessage((m) => {
    switch (m.t) {
      case "online":
        setDot(m.d.id, true);
        paintOnline();
        break;
      case "offline":
        setDot(m.d.id, false);
        paintOnline();
        break;
      case "presence":
        refreshAllDots();
        paintOnline();
        break;
    }
  });
  // Unsubscribe on detach (plan A10) — covers both leaving the screen and a tab
  // switch replacing this directory (body.replaceChildren() detaches `wrap`, so
  // the old subscription is gone before renderDirectory registers a new one).
  // On first mount `wrap` is attached synchronously above; when the whole screen
  // is still detached (initial navigation), watch from the next frame.
  requestAnimationFrame(() => {
    if (!wrap.isConnected) {
      off();
      return;
    }
    onDisconnect(wrap, off);
  });
  paintOnline();
}

function ratingText(e: DirectoryEntry): string {
  const parts: string[] = [];
  if (e.ratings.blitz) parts.push(`⚡ ${e.ratings.blitz.rating}${e.ratings.blitz.provisional ? "?" : ""}`);
  if (e.ratings.rapid) parts.push(`⏱ ${e.ratings.rapid.rating}${e.ratings.rapid.provisional ? "?" : ""}`);
  return parts.join(" · ") || t("players.unrated");
}

function describePrefs(p: ChallengePrefs): string {
  return `${p.tc} · ${t(`chess.${p.color}`)} · ${p.rated ? t("chess.rated") : t("chess.casual")}`;
}

// ---------------------------------------------------------------------------
// Leaderboard tab (unchanged render)
// ---------------------------------------------------------------------------

async function renderLeaderboard(body: HTMLElement, me: string): Promise<void> {
  const wrap = h(`
    <div>
      <p class="tagline">${t("players.lbTagline")}</p>
      <div class="boards"></div>
    </div>`);
  body.append(wrap);
  const boards = wrap.querySelector<HTMLElement>(".boards")!;
  try {
    const { boards: data } = await api.leaderboard();
    for (const [speed, labelKey] of SPEED_LABELS) {
      boards.append(renderBoard(t(labelKey), data[speed] ?? [], me));
    }
  } catch (err) {
    boards.textContent = err instanceof ApiError ? err.message : t("players.lbFailed");
  }
}

function renderBoard(label: string, rows: RankEntry[], me: string): HTMLElement {
  const box = h(`<div class="board"><h3>${esc(label)}</h3><div class="ranks"></div></div>`);
  const ranks = box.querySelector<HTMLElement>(".ranks")!;
  if (rows.length === 0) {
    ranks.innerHTML = `<p class="fine">${t("players.noRated", { speed: label.toLowerCase() })}</p>`;
    return box;
  }
  rows.forEach((r, i) => {
    const row = document.createElement("a");
    row.className = "rank-row" + (r.username === me ? " me" : "");
    row.setAttribute("data-link", "");
    row.href = `/player/${encodeURIComponent(r.username)}`;
    row.innerHTML = `
      <span class="rk">${i + 1}</span>
      <span class="pn">${esc(r.username)}</span>
      <span class="rt">${r.rating}${r.provisional ? "?" : ""}</span>
      <span class="gc">${r.games}</span>`;
    ranks.append(row);
  });
  return box;
}
